var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { useEffect, useRef, useState } from 'react';
import { InlineText, Text } from '@ui';
/**
 * Inline-editable plain-text cell for a free-text attachment field — Inspection's Technician and
 * Certification #, and whatever equivalents the remaining surfaces bring.
 *
 * Shared rather than per-surface for the same reason `InlineNameCell` is: `InlineText`'s `'text'`
 * variant is an UNCONTROLLED `contentEditable` div (see `InlineEditBase`), so React never re-syncs
 * its DOM text from a prop change after first render. Every revert therefore has to write
 * `textContent` back onto the captured blur target directly rather than just resetting state —
 * rules that drift the moment they are copied into a second surface.
 *
 * Differs from `InlineNameCell` in exactly one deliberate way: **blank is a legitimate value
 * here.** A name must never be cleared (E2E-27 asserts it reverts), but clearing a technician or a
 * certification number is a real edit and commits as `''`.
 */
export function InlineTextCell({ value, commit, placeholder }) {
    const [currentValue, setCurrentValue] = useState(value);
    const [error, setError] = useState(null);
    const isSavingRef = useRef(false);
    /** True between focus and blur — i.e. while the user is actually typing in this cell. */
    const isEditingRef = useRef(false);
    /** An external value that arrived mid-edit and must not be applied until the user is done. */
    const deferredValueRef = useRef(null);
    /** Monotonic commit id. Only the newest commit may write back. */
    const commitSeqRef = useRef(0);
    // Refetch-clobber guard. Two windows must be protected, not one:
    //   * while our own commit is in flight (`isSavingRef`), and
    //   * while the user is TYPING (`isEditingRef`).
    //
    // The second matters because `InlineText` renders `children` into a contentEditable and React
    // still reconciles that text: if `children` CHANGES between renders — which is exactly what a
    // listing refetch does — React overwrites the DOM and the half-typed edit is lost. (Text
    // survives a re-render only when `children` is unchanged, which is why this looks safe until a
    // refetch lands at the wrong moment.) The late value is stashed and applied on blur instead.
    useEffect(() => {
        if (isSavingRef.current || isEditingRef.current) {
            deferredValueRef.current = value;
            return;
        }
        setCurrentValue(value);
    }, [value]);
    const handleBlur = (event) => __awaiter(this, void 0, void 0, function* () {
        var _a;
        // Captured now: `event.currentTarget` is only guaranteed live for this handler's duration,
        // but the plain node reference keeps working afterwards.
        const target = event.currentTarget;
        const rawValue = ((_a = target.textContent) !== null && _a !== void 0 ? _a : '').trim();
        isEditingRef.current = false;
        if (rawValue === currentValue) {
            // No edit. If an external update arrived while the field was focused, this is the moment to
            // honour it rather than silently keep showing the stale text.
            const deferred = deferredValueRef.current;
            deferredValueRef.current = null;
            const next = deferred !== null && deferred !== void 0 ? deferred : currentValue;
            target.textContent = next;
            if (deferred !== null)
                setCurrentValue(deferred);
            return;
        }
        // The user DID edit, so their value wins over anything that arrived mid-edit.
        deferredValueRef.current = null;
        // Serialises overlapping commits. Blur -> refocus -> blur again can put two PATCHes in flight;
        // without this, the slower (older) response could land last and regress the displayed value.
        commitSeqRef.current += 1;
        const seq = commitSeqRef.current;
        isSavingRef.current = true;
        setError(null);
        const result = yield commit(rawValue);
        // A newer commit superseded this one — discard the stale result entirely.
        if (seq !== commitSeqRef.current)
            return;
        isSavingRef.current = false;
        if (result.success) {
            // Server is the source of truth — reflect its confirmed value verbatim, not the client's
            // optimistic one, which may differ (server-side trimming, normalisation).
            target.textContent = result.value;
            setCurrentValue(result.value);
            setError(null);
        }
        else {
            target.textContent = currentValue;
            setError(result.error);
        }
    });
    return (React.createElement("span", { className: "attachments-surface__text-cell", "data-action-cell": true },
        React.createElement(InlineText, { width: "full", placeholder: placeholder, onFocus: () => {
                isEditingRef.current = true;
            }, onBlur: (event) => {
                void handleBlur(event);
            } }, currentValue),
        error && (React.createElement(Text, { variant: "body", role: "alert", className: "attachments-surface__field-error attachments-surface__field-error--stacked" }, error))));
}
